// `configurar_preferencias`: el agente lee y cambia el formato y la calidad por defecto
// del usuario — ADR-2026-09-13-el-agente-configura-las-preferencias.
//
// POR QUÉ existe. Desde ADR-2026-09-13-generar-sin-preguntarme-en-la-card el usuario
// puede apagar las confirmaciones de un click. Cuando las apaga, la card NO APARECE — y
// la fila Calidad que vive en la card se vuelve inalcanzable. El único volante que le
// queda es la preferencia por defecto del menú "+", tres pantallas adentro. Esta tool es
// el atajo hablado a ese menú: "de ahora en más generá todo en 4K".
//
// QUÉ TOCA Y QUÉ NO. Las preferencias son reversibles y gratis: las cambia sin card. Los
// PERMISOS no: puede DEVOLVER las confirmaciones (si el usuario se arrepintió), nunca
// sacarlas. Un agente que se amplía los permisos hablando es un agente al que alcanza
// con convencer para dejar al usuario sin red, y del otro lado hay créditos suyos y
// filas en su catálogo. Sacarlas es acto del usuario: el botón blanco de la card o la
// hoja de Permisos.
//
// Todo PURO: el estado entra y sale por `DepsDePreferencias`, así se prueba con un
// objeto en memoria.

import {
  PERMISOS_POR_DEFECTO,
  type PermisosChat,
} from './confirmaciones';
import {
  aplicarCambioDePreferencias,
  CALIDADES_IMAGEN,
  CALIDADES_VIDEO,
  describirPreferencias,
  DURACION_MINIMA,
  RESOLUCIONES_VIDEO,
  SIN_PREFERENCIA,
  SLUGS_FORMATO_VIDEO,
  TIPOS_DE_SALIDA,
  type PreferenciasDeGeneracion,
} from './preferenciasDeGeneracion';
import { FORMATOS_IMAGEN } from './preferenciasDeGeneracion';
import { RESOLUCIONES } from '../lib/resolucionImagen';
import { VIDEO_BASICA_MAX_S, VIDEO_PROFESIONAL_MAX_S } from '../utils/credits';
import type { HerramientaChat } from './tipos';

export const NOMBRE_CONFIGURAR_PREFERENCIAS = 'configurar_preferencias';

/** De dónde salen y a dónde van los dos estados que la tool mira. */
export interface DepsDePreferencias {
  preferencias: () => PreferenciasDeGeneracion;
  guardarPreferencias: (p: PreferenciasDeGeneracion) => void;
  permisos: () => PermisosChat;
  guardarPermisos: (p: PermisosChat) => void;
}

const listar = (xs: readonly string[]): string => xs.join(' / ');

export const HERRAMIENTA_CONFIGURAR_PREFERENCIAS: HerramientaChat = {
  name: NOMBRE_CONFIGURAR_PREFERENCIAS,
  description:
    'Cambia las PREFERENCIAS POR DEFECTO de generación del usuario: si está haciendo imágenes o videos, y con qué formato, calidad y duración se genera cuando no pide otra cosa. Son las mismas que él elige en las pastillas de la caja y en el menú "+" → Preferencias de contenido, y las tenés a la vista en cada turno (bloque "PREFERENCIAS DEL USUARIO PARA GENERAR"). Es instantáneo y GRATIS: no gasta créditos, no genera nada y no hay card que confirmar.\n' +
    'USALA cuando el usuario pide un default, no una generación puntual: "de ahora en más generá todo en 4K", "siempre en story", "poné los videos en calidad básica", "volvé a la calidad normal". Si sólo quiere ESTA imagen en 4K, NO la uses: pasá `quality` en la tool de generación y listo — cambiar el default por un pedido puntual le sube el costo de todo lo que venga después.\n' +
    'Mandá SÓLO los campos que cambian; los que no mandás quedan como están. Para volver un campo al default del sistema, mandá "' +
    SIN_PREFERENCIA +
    '".\n' +
    `Valores: tipoDeSalida (${listar(TIPOS_DE_SALIDA.map((t) => t.id))}) · formatoImagen (${listar(FORMATOS_IMAGEN.map((f) => f.id))}) · calidadImagen (${listar(RESOLUCIONES)}; 1K y 2K = 1 crédito, 4K = 2 por imagen; el default es 2K) · formatoVideo (${listar(SLUGS_FORMATO_VIDEO)}) · calidadVideo (${listar(CALIDADES_VIDEO.map((c) => c.id))}) · resolucionVideo (${listar(RESOLUCIONES_VIDEO.map((r) => r.id))}; cambia el costo por segundo; 480p en Profesional es borrador: se puede pasar a 1080p después) · duracionVideo (segundos, hasta ${VIDEO_BASICA_MAX_S} en Estándar y ${VIDEO_PROFESIONAL_MAX_S} en Profesional).\n` +
    'CONFIRMACIONES: con `restaurar_confirmaciones: true` volvés a dejar que el chat le pregunte antes de cada generación, de crear productos y de investigar — usalo si dice algo como "volvé a preguntarme antes de generar". Lo contrario NO lo podés hacer: APAGAR las confirmaciones no está en esta tool ni en ninguna otra. Si te pide que dejes de preguntarle, decíle que toque el botón "Generar sin preguntarme" que aparece abajo del botón de generar en la card, o que lo cambie en el menú "+" → Permisos. No lo intentes por acá.\n' +
    'Devuelve qué cambió, qué rechazó y cómo quedaron las preferencias. Contale al usuario lo que dice la respuesta — si algo salió rechazado, decile el valor válido en vez de dar por hecho que se aplicó.',
  input_schema: {
    type: 'object',
    properties: {
      tipoDeSalida: {
        type: 'string',
        description: `Qué está por generar el usuario: ${listar(
          TIPOS_DE_SALIDA.map((t) => `${t.id} (${t.etiqueta})`),
        )}. Es el switch de la caja, lo tenés a la vista en cada turno. "agente" es el default y quiere decir que NO eligió: ahí decidís vos por el pedido. Cambialo si te dice que de ahora en más quiere lo otro ("pasemos a videos"); NO lo toques por un pedido puntual, que eso lo resolvés eligiendo la tool. Este campo NO acepta "${SIN_PREFERENCIA}": "agente" ya ES la posición de "que decida el sistema".`,
      },
      formatoImagen: {
        type: 'string',
        description: `Formato por defecto de las IMÁGENES: ${listar(
          FORMATOS_IMAGEN.map((f) => `${f.id} (${f.etiqueta})`),
        )}, o "${SIN_PREFERENCIA}" para que vuelva a usar el último formato que usó.`,
      },
      calidadImagen: {
        type: 'string',
        description: `Calidad por defecto de las IMÁGENES: ${listar(
          CALIDADES_IMAGEN.map((c) => `${c.id} = ${c.etiqueta} (${c.detalle})`),
        )}, o "${SIN_PREFERENCIA}" para volver al default (2K). OJO: 4K duplica el costo de CADA imagen.`,
      },
      formatoVideo: {
        type: 'string',
        description: `Formato por defecto de los VIDEOS: ${listar(
          SLUGS_FORMATO_VIDEO,
        )}, o "${SIN_PREFERENCIA}" para volver a story.`,
      },
      calidadVideo: {
        type: 'string',
        description: `Calidad por defecto de los VIDEOS: ${listar(
          CALIDADES_VIDEO.map((c) => `${c.id} = ${c.etiqueta} (${c.detalle})`),
        )}, o "${SIN_PREFERENCIA}" para volver a profesional.`,
      },
      resolucionVideo: {
        type: 'string',
        description: `Resolución por defecto de los VIDEOS: ${listar(
          RESOLUCIONES_VIDEO.map((r) => `${r.id} (${r.detalle})`),
        )}, o "${SIN_PREFERENCIA}" para volver a 720p. Es la misma que mandás en \`resolution\` de generate_video. OJO que el video se cobra POR SEGUNDO: 1080p cuesta casi el doble que 720p en cada video.`,
      },
      duracionVideo: {
        // NÚMERO o STRING, y el string es a propósito: el reseteo de todos los campos es
        // la palabra "ninguna", y con `type: 'number'` pelado el agente no tenía cómo
        // mandarla sin contradecir el esquema
        // (ADR-2026-09-24-el-reseteo-de-la-duracion-entra-en-el-esquema). La rama
        // numérica de `aplicarCambioDePreferencias` ya redondea y valida contra el tope
        // vivo, así que un "8" en texto entra igual que un 8.
        type: ['number', 'string'],
        description: `Duración por defecto de los VIDEOS, en SEGUNDOS: cualquier entero desde ${DURACION_MINIMA} (el usuario la elige con un slider, al segundo). Tope ${VIDEO_BASICA_MAX_S} s en calidad Estándar y ${VIDEO_PROFESIONAL_MAX_S} s en Profesional — si bajás la calidad, la duración se recorta sola. "${SIN_PREFERENCIA}" vuelve a 5 s. OJO que el video se cobra POR SEGUNDO: duplicar la duración duplica el costo de cada video que venga.`,
      },
      restaurar_confirmaciones: {
        type: 'boolean',
        description:
          'true = volver a preguntarle antes de generar, de crear productos y de investigar. Es lo ÚNICO que podés hacer con las confirmaciones: no existe el valor que las apague (eso lo hace el usuario con el botón "Generar sin preguntarme" de la card o desde el menú "+" → Permisos).',
      },
    },
  },
};

/** Lo que vuelve como `tool_result`. Todo legible: el agente lo repite en su respuesta. */
export type ResultadoPreferencias = {
  ok: boolean;
  cambios: string[];
  rechazos?: string[];
  preferencias_ahora: string;
  confirmaciones?: string;
  nota?: string;
};

/** Cómo quedaron las confirmaciones, en una línea que el agente pueda repetir. */
const describirConfirmaciones = (p: PermisosChat): string =>
  `Ahora te pregunta antes de generar (${p.generacion}), de sumar productos (${p.productos}) y de investigar (${p.investigacion}).`;

/**
 * Ejecuta `configurar_preferencias`. Guarda sólo si algo cambió de verdad, para no
 * escribir el storage —ni decirle al usuario que cambió algo— cuando pidió lo que ya
 * tenía puesto.
 */
export function resolverConfigurarPreferencias(
  entrada: unknown,
  deps: DepsDePreferencias,
): ResultadoPreferencias {
  const pedido = (entrada && typeof entrada === 'object' ? entrada : {}) as Record<
    string,
    unknown
  >;
  const { siguiente, cambios, rechazos } = aplicarCambioDePreferencias(
    deps.preferencias(),
    pedido,
  );
  if (cambios.length > 0) deps.guardarPreferencias(siguiente);

  const notas: string[] = [];
  let confirmaciones: string | undefined;

  // Restaurar la red de seguridad. `=== true` a propósito: cualquier otra cosa —false,
  // "nunca", un objeto— NO afloja nada. No hay rama que baje permisos en esta función.
  if (pedido.restaurar_confirmaciones === true) {
    const antes = deps.permisos();
    const iguales = (Object.keys(PERMISOS_POR_DEFECTO) as Array<keyof PermisosChat>).every(
      (k) => antes[k] === PERMISOS_POR_DEFECTO[k],
    );
    if (iguales) {
      notas.push('Las confirmaciones ya estaban puestas: no hubo nada que restaurar.');
    } else {
      deps.guardarPermisos({ ...PERMISOS_POR_DEFECTO });
      cambios.push('Confirmaciones: vuelve a preguntar antes de cada acción');
    }
    confirmaciones = describirConfirmaciones(PERMISOS_POR_DEFECTO);
  } else if ('restaurar_confirmaciones' in pedido) {
    // Pidió lo otro: apagarlas. Se le contesta dónde se toca, y no se toca nada.
    notas.push(
      'Las confirmaciones no se apagan desde acá. Decile que toque "Generar sin preguntarme" abajo del botón de generar en la card, o que lo cambie en el menú "+" → Permisos.',
    );
    confirmaciones = describirConfirmaciones(deps.permisos());
  }

  if (cambios.length === 0 && rechazos.length === 0 && notas.length === 0) {
    notas.push('No hubo nada que cambiar: ya estaba así.');
  }

  return {
    ok: rechazos.length === 0,
    cambios,
    ...(rechazos.length > 0 ? { rechazos } : {}),
    preferencias_ahora: describirPreferencias(cambios.length > 0 ? siguiente : deps.preferencias()),
    ...(confirmaciones ? { confirmaciones } : {}),
    ...(notas.length > 0 ? { nota: notas.join(' ') } : {}),
  };
}
